<script setup>
import { shallowRef, watch } from "vue";

import { projects as projectsApi } from "~/api";
import GlAlert from "~/components/gitlab/GlAlert.vue";
import GlLoadingIcon from "~/components/gitlab/GlLoadingIcon.vue";
import GlPagination from "~/components/gitlab/GlPagination.vue";
import EmptyState from "~/components/EmptyState.vue";
import ProjectCard from "~/components/ProjectCard.vue";
import { useProjectActions } from "~/composables/useProjectActions";
import { fetch } from "~/utils/api.js";

const { error, isAdmin, loading, pagination, projects } = defineProps({
  error: {
    default: null,
    type: String
  },
  isAdmin: {
    default: false,
    type: Boolean
  },
  loading: {
    default: false,
    type: Boolean
  },
  pagination: {
    default: null,
    type: Object
  },
  projects: {
    default: () => [],
    type: Array
  }
});

const emit = defineEmits(["dismiss-error", "paginate", "delete-request", "sync-done"]);
const syncAlert = shallowRef(null);
const localProjects = shallowRef(projects.map((p) => ({ ...p })));

watch(
  () => projects,
  (newProjects) => {
    localProjects.value = newProjects.map((p) => ({ ...p }));
  }
);

function updateProject(id, patch) {
  localProjects.value = localProjects.value.map((p) => (p.id === id ? { ...p, ...patch } : p));
}

const { actionAlert: toggleAlert, handleToggle } = useProjectActions({
  onToggle: (id, enabled) => updateProject(id, { enabled })
});

async function onSyncRequest({ projectId }) {
  syncAlert.value = null;
  await fetch({
    alert: syncAlert,
    apiMethod: projectsApi.update,
    onSuccess: (data) => {
      syncAlert.value = { message: data.message, type: data.type };
      emit("sync-done");
    },
    requestArgs: { params: { id: projectId } }
  });
}
</script>

<template>
  <div>
    <!-- Sync action alert -->
    <GlAlert v-if="syncAlert" :variant="syncAlert.type" class="mb-4" @dismiss="syncAlert = null">
      {{ syncAlert.message }}
    </GlAlert>

    <!-- Toggle action alert -->
    <GlAlert v-if="toggleAlert" :variant="toggleAlert.type" class="mb-4" @dismiss="toggleAlert = null">
      {{ toggleAlert.message }}
    </GlAlert>

    <!-- Error from fetch -->
    <GlAlert v-if="error" variant="danger" class="mb-4" @dismiss="emit('dismiss-error')">{{ error }}</GlAlert>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center py-12">
      <GlLoadingIcon size="lg" />
    </div>

    <template v-else>
      <!-- Empty state -->
      <EmptyState
        v-if="projects.length === 0"
        title="No projects found"
        description="Try adjusting the filter or add a new project."
      />

      <!-- Project list + pagination -->
      <template v-else>
        <ul
          class="list-none p-0 border-t border-[var(--gl-border-color-default)] bg-[var(--gl-background-color-default)]"
        >
          <ProjectCard
            v-for="project in localProjects"
            :key="project.id"
            :project="project"
            :is-admin="isAdmin"
            @delete-request="emit('delete-request', $event)"
            @sync-request="onSyncRequest"
            @toggle="handleToggle"
          />
        </ul>

        <GlPagination
          v-if="pagination && pagination.pages > 1"
          :model-value="pagination.page"
          :pages="pagination.pages"
          :series="pagination.series"
          @update:model-value="(page) => emit('paginate', { page })"
        />
      </template>
    </template>
  </div>
</template>
